import axios from 'axios';
import { authRole } from '../../@crema/constants/AppConst';
import axiosInstance from '../api/axiosInstance';
import { AuthUser, BackendUserData } from '../types/auth';

interface TokenResponse {
    access_token: string;
    refresh_token: string;
    id_token?: string;
}

export const exchangeCodeForToken = async (
    code: string,
    redirectUri: string,

    codeVerifier: string,
): Promise<TokenResponse> => {
    const tokenEndpoint = `${import.meta.env.VITE__KEYCLOAK_LOGIN_BASE_URL}/token`;

    const clientId = import.meta.env.VITE__KEYCLOAK_CLIENT_ID;

    const response = await axios.post(
        tokenEndpoint,
        new URLSearchParams({
            grant_type: 'authorization_code',
            code,
            redirect_uri: redirectUri,
            client_id: clientId,
            code_verifier: codeVerifier,
        }),
        {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        },
    );

    return response.data;
};

export const refreshTokenWithKeycloak = async (
    refreshToken: string,
    clientId: string,
    tokenEndpoint: string,
): Promise<TokenResponse> => {
    const response = await axios.post(
        tokenEndpoint,
        new URLSearchParams({
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
            client_id: clientId,
        }),
        {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        },
    );
    return response.data;
};

export const getUserFromKeycloak = (
    user: BackendUserData,
): AuthUser | undefined => {
    if (user) {
        const role = getUserRole(user.roleList);
        return {
            id: user.id,
            uid: user.id.toString(),
            displayName: user.fullName,
            email: user.email,
            photoURL: user?.photoURL ?? '/assets/images/avatar/A11.jpg',
            role: role,
            companyId: user.companyId,
            companyName: user.companyName,
            departmentId: user.departmentId,
            departmentName: user.departmentName,
            username: user.username, // Add backend `username`
            phoneNo: user.phoneNo, // Add backend `phoneNo`
            designationId: user.designationId, // Add backend `designationId`
            designationName: user.designationName, // Add backend `designationName`
            isPiaSigned: user.isPiaSigned, // Add backend `isPiaSigned`
            hasDueInvoice: user.hasDueInvoice, // Add backend `hasDueInvoice`
            dueInvoiceList: user.dueInvoiceList, // Add backend `dueInvoiceList`
            roleList: user.roleList, // Add backend `roleList`
            active: user.active,
            companyConfig: user.companyConfig,
        };
    }
};

const getUserRole = (
    roleList: {
        roleId: number;
        roleName: string;
        rolePriority: string | null;
    }[],
) => {
    const isSuperAdmin = roleList.find(
        (role) => role.roleName === 'SuperAdmin',
    );
    const isCompanyAdmin =
        !isSuperAdmin &&
        roleList.find((role) => role.roleName === 'CompanyAdmin');

    const isDepartmentAdmin =
        !isSuperAdmin &&
        !isCompanyAdmin &&
        roleList.find((role) => role.roleName === 'DepartmentAdmin');

    const isZoneAdmin =
        !isSuperAdmin &&
        !isCompanyAdmin &&
        roleList.find((role) => role.roleName === 'ZoneAdmin');

    let userRole: string[] = [];

    if (isSuperAdmin) {
        userRole = authRole.SuperAdmin;
    } else if (isCompanyAdmin) {
        userRole = authRole.CompanyAdmin;
    } else if (isDepartmentAdmin) {
        userRole = authRole.DepartmentAdmin;
    } else if (isZoneAdmin) {
        userRole = authRole.ZoneAdmin;
    }

    return userRole;
};

export const getUserInformation = async (
    token: string,
): Promise<BackendUserData> => {
    try {
        const response = await axiosInstance.get('/user-management/me', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data.data;
    } catch (error) {
        console.error('Error fetching user information:', error);
        throw error;
    }
};
